import { createApplicationService } from "./base/applicationServiceBase";
import { AccountsAPI } from "api/axios.config";
import { useMemo } from "react";

//-------------------------------------------
//           API Functions
//-------------------------------------------

const NOMINALS_BASE = "/nominals";

// Fetch all active nominals - SINGLE API CALL
const fetchAllNominalsAPI = () =>
    AccountsAPI.get(`${NOMINALS_BASE}?archived=false&active=true`);

const addNewNominalAPI = (data) =>
    AccountsAPI.post(NOMINALS_BASE, JSON.stringify({ data }), {
        headers: { "Content-Type": "application/json" },
    });

const updateNominalAPI = (id, data) =>
    AccountsAPI.patch(`${NOMINALS_BASE}/${id}`, JSON.stringify({ data }), {
        headers: { "Content-Type": "application/json" },
    });

// Get single nominal by ID
const getViewNominalAPI = (id) =>
    AccountsAPI.get(`${NOMINALS_BASE}/${id}`);

// Get nominal balances
const getNominalBalancesAPI = (nominalRef, FromDate, ToDate) =>
    AccountsAPI.get(`${NOMINALS_BASE}/Ref/${nominalRef}/Balances?FromDate=${FromDate}&ToDate=${ToDate}`);

//-------------------------------------------
//           Single Base Service
//-------------------------------------------

// Main nominals service - fetches ALL nominals once
const nominalsService = createApplicationService({
    name: "nominals",
    apiFetchFn: fetchAllNominalsAPI,
    apiAddFn: addNewNominalAPI,
    apiUpdateFn: updateNominalAPI,
});

//-------------------------------------------
//           Single Reducer
//-------------------------------------------

const nominalsReducer = nominalsService.reducer;

//-------------------------------------------
//           Core Functions
//-------------------------------------------

const getAllNominals = nominalsService.getData;
const addNominal = nominalsService.addData;
const updateNominal = nominalsService.updateData;
const useAllNominals = nominalsService.useData;
const resetNominals = nominalsService.resetData;

// Additional service functions
const getViewNominal = async (id) => {
    if (!id) {
        console.error("getViewNominal: No ID provided");
        return { success: false, error: "No ID provided" };
    }

    try {
        const response = await getViewNominalAPI(id);
        // Check if data is nested in response.data.data
        const data = response.data?.data || response.data;
        return { success: true, data: data };
    } catch (error) {
        console.error("Error fetching nominal:", error);
        return { success: false, error };
    }
};

const getNominalBalances = async (nominalRef, FromDate, ToDate) => {
    try {
        const response = await getNominalBalancesAPI(nominalRef, FromDate, ToDate);
        return { success: true, data: response.data };
    } catch (error) {
        console.error("Error fetching nominal balances:", error);
        return { success: false, error };
    }
};

// Get nominals list with parameters (for search/filtering) - just API calls
const getNominalsList = async (parameters = "") => {
    try {
        let url = `/nominals?`;
        if (parameters !== "") {
            url += `${parameters}`;
        }
        const response = await AccountsAPI.get(url);
        return { success: true, data: response.data };
    } catch (error) {
        console.error("Error fetching nominals list:", error);
        return { success: false, error };
    }
};

//-------------------------------------------
//           Filtered Hook (Client-side filtering)
//-------------------------------------------

// Single generic hook for any nominal subType
const useNominalsBySubType = (subType) => {
    const { data, loading } = useAllNominals();

    const filteredData = useMemo(() => {
        if (!subType) return data; // Return all if no subType specified

        return data.filter(item => item.subType === subType);
    }, [data, subType]);

    return { data: filteredData, loading };
};


const useNonControlNominals = () => {
    const { data, loading } = useAllNominals();

    const filteredData = useMemo(() => {
        return data?.filter(item => item.control === false);
    }, [data]);

    return { data: filteredData, loading };
};

//-------------------------------------------
//           Reset Functions
//-------------------------------------------

// Function to reset nominals service (useful when nominals are updated)
const resetAllNominals = () => {
    resetNominals();
};

//-------------------------------------------
//           Exports
//-------------------------------------------

export {
    // Single reducer
    nominalsReducer,

    // Core functions
    getAllNominals,
    addNominal,
    updateNominal,
    useAllNominals,
    getViewNominal,
    getNominalBalances,
    getNominalsList,

    // Single filtered hook for UI components (client-side filtering)
    useNominalsBySubType,
    useNonControlNominals,

    // Reset functions
    resetNominals,
    resetAllNominals,
};
